import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown } from "lucide-react";
import useAnchoredPosition from "../../hooks/useAnchoredPosition";
import {
  FEE_FRAME_PRESETS,
  FEE_FRAME_CURRENCIES,
  parseFeeShorthand,
  formatFeeFrame,
  formatFeeAmount,
} from "../../utils/scoutFeeFrame";

// Fee-frame dropdown (design 7a): one field, two paths — quick presets on top, a
// custom frame (currency + optional Min/Max, shorthand like "750k"/"1.2M") below.
// Presets are internally Custom-with-fixed-values (one data model), so both paths
// emit the SAME payload shape the write serializer accepts:
//   { fee_frame_kind, fee_frame_currency, fee_frame_min, fee_frame_max }
// `value` is that payload (may be empty → defaults to "Open"). Never blocking.

const payloadToFrame = (v) => ({
  kind: v?.fee_frame_kind || "open",
  currency: v?.fee_frame_currency || "",
  min: v?.fee_frame_min ?? null,
  max: v?.fee_frame_max ?? null,
});

// Which preset (if any) a payload equals — so re-opening shows the right check.
const matchingPresetId = (v, presets) => {
  const p = payloadToFrame(v);
  const hit = presets.find((preset) => {
    const f = payloadToFrame(preset.payload);
    return f.kind === p.kind && (f.currency || "") === (p.currency || "")
      && (f.min ?? null) === (p.min ?? null) && (f.max ?? null) === (p.max ?? null);
  });
  return hit ? hit.id : null;
};

// `presets`/`labelPrefix` are OPTIONAL props (default = the Scout fee-frame consts)
// so Scout Requests renders byte-identically, while TP-Last-Minute passes its own
// salary-band presets + label without a parallel component (DECISIONS D-006).
// LM composer additions (design 4b, additive — Scout renders byte-identically):
// `renderTrigger` swaps the boxed trigger for caller markup (the dashed "Custom"
// pill), `hidePresetSection` drops the panel's preset list (the LM composer shows
// presets as inline pills instead), `matchPanelWidth:false` + `panelMinWidth`
// size the panel independently of a small pill trigger.
const FeeFrameSelect = ({
  value,
  onChange,
  presets = FEE_FRAME_PRESETS,
  labelPrefix = "Custom",
  renderTrigger = null,
  hidePresetSection = false,
  matchPanelWidth = true,
  panelMinWidth = null,
}) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef(null);
  const presetId = matchingPresetId(value, presets);
  const isCustom = !presetId; // a non-preset range is a custom frame

  // Panel is portalled to document.body and positioned against the trigger, so
  // the modal's overflow-y-auto body can never clip it (same D-003 pattern as
  // CustomDropdown).
  const { panelRef, style: menuStyle } = useAnchoredPosition(open, triggerRef, {
    align: "left",
    gap: 4,
    matchTriggerWidth: matchPanelWidth,
  });

  // Custom-frame draft, prefilled from the current value when it is a custom
  // range (N-4) so re-opening a saved frame shows its currency + bounds, not blanks.
  // The modal remounts this component per open, so the init runs fresh each time.
  const [ccy, setCcy] = useState(value?.fee_frame_currency || "EUR");
  const [minStr, setMinStr] = useState(
    isCustom && value?.fee_frame_min != null ? formatFeeAmount(value.fee_frame_min) : "",
  );
  const [maxStr, setMaxStr] = useState(
    isCustom && value?.fee_frame_max != null ? formatFeeAmount(value.fee_frame_max) : "",
  );
  const [customError, setCustomError] = useState("");

  useEffect(() => {
    const onDoc = (e) => {
      const inTrigger = triggerRef.current && triggerRef.current.contains(e.target);
      const inPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!inTrigger && !inPanel) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [panelRef]);

  const currentLabel = () => {
    const body = formatFeeFrame(payloadToFrame(value));
    if (presetId) {
      const preset = presets.find((p) => p.id === presetId);
      return preset ? preset.label : body;
    }
    return `${labelPrefix}: ${body}`;
  };

  const choosePreset = (preset) => {
    onChange(preset.payload);
    setCustomError("");
    setOpen(false);
  };

  const applyCustom = () => {
    const min = minStr.trim() ? parseFeeShorthand(minStr) : null;
    const max = maxStr.trim() ? parseFeeShorthand(maxStr) : null;
    if (minStr.trim() && min === null) return setCustomError("Enter a valid minimum, e.g. 750k.");
    if (maxStr.trim() && max === null) return setCustomError("Enter a valid maximum, e.g. 1.2M.");
    if (min === null && max === null) return setCustomError("Add at least a minimum or a maximum.");
    if (min !== null && max !== null && min > max) return setCustomError("Min must be at most the max.");
    onChange({ fee_frame_kind: "range", fee_frame_currency: ccy, fee_frame_min: min, fee_frame_max: max });
    setCustomError("");
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={
          renderTrigger
            ? "inline-flex"
            : `w-full flex items-center justify-between px-3.5 py-2.5 rounded-lg bg-[#F3F4F6] text-[14px] font-medium text-[#111827] transition ${
                open ? "ring-2 ring-[#2283FF]/25 border border-[#2283FF]" : "border border-transparent"
              }`
        }
      >
        {renderTrigger ? (
          renderTrigger({ open, label: currentLabel(), isCustom })
        ) : (
          <>
            <span>{currentLabel()}</span>
            <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
          </>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{
            ...(menuStyle || { position: "fixed", top: 0, left: 0, visibility: "hidden" }),
            ...(panelMinWidth ? { minWidth: panelMinWidth } : {}),
          }}
          className="z-[9999] bg-white border border-[#E5E7EB] rounded-[10px] shadow-[0_8px_24px_rgba(17,24,39,0.12)] overflow-hidden"
        >
          {/* PRESETS */}
          {!hidePresetSection && (
            <>
              <div className="px-3 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.07em] text-[#9CA3AF]">
                Presets
              </div>
              {presets.map((preset) => {
                const active = presetId === preset.id;
                return (
                  <button
                    key={preset.id}
                    type="button"
                    onClick={() => choosePreset(preset)}
                    className={`w-full flex items-center justify-between px-3.5 py-2 text-left text-[13.5px] hover:bg-gray-50 ${
                      active ? "text-[#1B6FE0] font-semibold" : "text-[#111827]"
                    }`}
                  >
                    {preset.label}
                    {active && <Check className="w-4 h-4 text-[#2283FF]" />}
                  </button>
                );
              })}
            </>
          )}

          {/* CUSTOM FRAME */}
          <div className={`px-3.5 pt-3 pb-3.5 ${hidePresetSection ? "" : "border-t border-[#E5E7EB] mt-1"}`}>
            <div className="flex items-center gap-1.5 mb-2">
              <span className="text-[10px] font-bold uppercase tracking-[0.07em] text-[#9CA3AF]">
                Custom frame
              </span>
              {isCustom && <Check className="w-3.5 h-3.5 text-[#2283FF]" />}
            </div>
            <div className="flex items-center gap-2">
              <select
                value={ccy}
                onChange={(e) => setCcy(e.target.value)}
                className="px-2 py-1.5 rounded-md bg-[#F3F4F6] text-[13px] font-semibold text-[#111827] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
                aria-label="Currency"
              >
                {FEE_FRAME_CURRENCIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>
              <input
                type="text"
                inputMode="decimal"
                value={minStr}
                onChange={(e) => setMinStr(e.target.value)}
                placeholder="Min"
                className="w-full min-w-0 px-2.5 py-1.5 rounded-md bg-[#F3F4F6] text-[13px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
              />
              <span className="text-[#9CA3AF]">–</span>
              <input
                type="text"
                inputMode="decimal"
                value={maxStr}
                onChange={(e) => setMaxStr(e.target.value)}
                placeholder="Max"
                className="w-full min-w-0 px-2.5 py-1.5 rounded-md bg-[#F3F4F6] text-[13px] text-[#111827] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-2 focus:ring-[#2283FF]/25"
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-[11.5px] text-[#6B7280]">Shorthand works: 750k · 1.2M</span>
              <button
                type="button"
                onClick={applyCustom}
                className="px-3 py-1.5 rounded-lg bg-[#2283FF] text-white text-[12.5px] font-semibold hover:bg-[#1B6FE0]"
              >
                Apply
              </button>
            </div>
            {customError && <p className="mt-1.5 text-[11.5px] text-[#DC2626]">{customError}</p>}
            <p className="mt-2 text-[11.5px] text-[#9CA3AF] leading-snug">
              No conversion: agents see the frame in the currency you set.
            </p>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};

export default FeeFrameSelect;
